import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { StorageHandler } from '@/core/data_handlers/storage_handler';
import { Logger } from '@/shared/logger/logger';

export type IShopifyCartItem = {
  currency: string;
  final_line_price?: number;
  final_price?: number;
  gift_card?: boolean;
  grams?: number;
  handle?: string;
  id: string;
  image?: string;
  line_price?: number;
  options_with_values?: any[];
  price?: number;
  product_description?: string;
  product_has_only_default_variant?: boolean;
  product_id: string;
  product_title?: string;
  product_type?: string;
  properties?: Record<string, any>;
  quantity: number;
  requires_shipping?: boolean;
  sku?: string;
  tax_price?: number;
  taxable?: boolean;
  title: string;
  url?: string;
  variant_id: string;
  variant_options?: string[];
  variant_title?: string;
  vendor?: string;
};

export type IShopifyCartState = {
  attributes?: Record<string, any>;
  cart_level_discount_applications?: any[];
  currency?: string;
  item_count: number;
  items: IShopifyCartItem[];
  items_subtotal_price?: number;
  last_updated: number;
  note?: string;
  original_total_price?: number;
  original_total_price_includes_discount?: boolean;
  requires_shipping?: boolean;
  token?: string;
  total_discount?: number;
  total_price: number;
  total_weight?: number;
};

export type CartChangeDetection = {
  addedItems: IShopifyCartItem[];
  changed: boolean;
  currentCount: number;
  previousCount: number;
  removedItems: IShopifyCartItem[];
  updatedItems: IShopifyCartItem[];
};

/**
 * Singleton class to handle Shopify cart operations
 */
export class ShopifyCartHandler {
  private static instance: ShopifyCartHandler;
  private currentCartState: IShopifyCartState | null = null;
  private readonly STORAGE_NAMESPACE = 'shpfy_crt';
  private readonly cartStorageHandler: StorageHandler<IShopifyCartState>;
  private readonly initialCartState: IShopifyCartState = {
    item_count: 0,
    items: [],
    last_updated: 0,
    total_price: 0,
  };
  private constructor() {
    this.cartStorageHandler = new StorageHandler<IShopifyCartState>(
      this.STORAGE_NAMESPACE,
      this.initialCartState,
      EBrowserStorageType.LOCALSTORAGE,
    );

    // Load cart state from storage if available
    this.loadCartState();
  }

  public static getInstance(): ShopifyCartHandler {
    if (!ShopifyCartHandler.instance) {
      ShopifyCartHandler.instance = new ShopifyCartHandler();
    }
    return ShopifyCartHandler.instance;
  }

  private loadCartState(): void {
    try {
      this.currentCartState = this.cartStorageHandler.get();
    } catch (error) {
      Logger.error('Failed to load cart state from storage', error);
    }
  }

  /**
   * Get the current cart state
   */
  public getCurrentCartState(): IShopifyCartState {
    return this.currentCartState ?? this.initialCartState;
  }

  /**
   * Update the cart state from provided data
   */
  public updateCartStateFromData(cartData: IShopifyCartState): void {
    // Log the cart data before updating to help with debugging
    Logger.debug('Updating cart state with data:', {
      item_count: cartData.item_count,
      items_count: cartData.items?.length || 0,
      total_price: cartData.total_price,
    });
    const currentState = this.cartStorageHandler.get();

    // Make sure we have valid data before updating
    if (
      cartData &&
      typeof cartData === 'object' &&
      currentState.last_updated < cartData.last_updated
    ) {
      // Create a new cart state with default values for required properties
      this.currentCartState = {
        ...cartData,
        item_count: cartData.item_count || 0,
        items: cartData.items || [],
        last_updated: cartData.last_updated || Date.now(),
        total_price: cartData.total_price || 0,
      };

      try {
        // Store the updated cart state
        this.cartStorageHandler.set(this.currentCartState);

        // Verify the data was stored correctly
        const storedCartState = this.cartStorageHandler.get();
        Logger.debug('Cart state stored successfully:', {
          expected_item_count: this.currentCartState.item_count,
          stored_item_count: storedCartState.item_count,
        });
      } catch (error) {
        Logger.error('Failed to update cart state in storage', error);
      }
    } else {
      Logger.error('Invalid cart data provided for update', cartData);
    }
  }

  /**
   * Detect changes between the current cart state and a new cart state
   */
  public detectCartChanges(
    newCartState: IShopifyCartState,
  ): CartChangeDetection {
    if (!this.currentCartState) {
      return {
        addedItems: newCartState.items,
        changed: newCartState.item_count > 0,
        currentCount: newCartState.item_count,
        previousCount: 0,
        removedItems: [],
        updatedItems: [],
      };
    }

    const previousItems = this.currentCartState.items || [];
    const currentItems = newCartState.items || [];

    // Find added items (in current but not in previous)
    const addedItems = currentItems.filter(
      currentItem =>
        !previousItems.some(
          prevItem =>
            this.getCartItemKey(prevItem) ===
              this.getCartItemKey(currentItem) &&
            prevItem.quantity >= currentItem.quantity,
        ),
    );

    // Find removed items (in previous but not in current)
    const removedItems = previousItems.filter(
      prevItem =>
        !currentItems.some(
          currentItem =>
            this.getCartItemKey(currentItem) === this.getCartItemKey(prevItem),
        ),
    );

    // Find updated items (in both but quantity changed)
    const updatedItems = currentItems.filter(currentItem => {
      const prevItem = previousItems.find(
        pItem =>
          this.getCartItemKey(pItem) === this.getCartItemKey(currentItem),
      );
      return prevItem && prevItem.quantity !== currentItem.quantity;
    });

    const previousCount = this.currentCartState.item_count || 0;
    const currentCount = newCartState.item_count || 0;

    return {
      addedItems,
      changed:
        addedItems.length > 0 ||
        removedItems.length > 0 ||
        updatedItems.length > 0,
      currentCount,
      previousCount,
      removedItems,
      updatedItems,
    };
  }

  public getCartItemKey(cartItem: IShopifyCartItem) {
    return `${cartItem.product_id}_${cartItem.variant_id}`;
  }
}

export default ShopifyCartHandler;
